import moment from 'moment';
import React, { useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import TableActionButton from 'components/TableActionButton';
import ThemedSelect from 'components/ThemedSelect';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import {
  FilterOverlay,
  FilterDropdown,
  FilterSection,
  FilterTitle,
  FilterRange,
  FilterActions,
} from 'pages/track/Customers/components/CustomerTable/StyledComponents';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import { useGetAccountMembersReport } from '../Members/gql/useGetAccountMembersReport';
import { useGetAccountPurchaseReport } from '../Purchases/gql/useGetAccountPurchaseReport.mutation';
import { PrintAnnualPurchaseReport } from '../Purchases/PrintAnnualPurchaseReport';
import {
  Sidebar,
  WaiverBadge,
} from '../Purchases/PurchaseDetailPage/StyledComponents';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  HeaderContainer,
  HeaderTitle,
  ContentWrapper,
  PageHeader,
  FilterRow,
  FilterContainer,
} from '../Purchases/PurchasesPage/StyledComponents';
import { useGetAccountAnnuals } from './gql/useGetAccountAnnuals';
import { useGetAccountMemberships } from './gql/useGetAccountMemberships';

const isTicketHoss = 'tickethoss' === process.env.REACT_APP_PLATFORM;
const annualLabelPlural = isTicketHoss ? 'All Tickets' : 'All Passes';

// Convert YYYY-MM-DD to MM/DD/YYYY for the GraphQL Date scalar
const toGraphQLDate = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-');
  return `${m}-${d}-${y}`;
};

// Icon for page header
const AnnualsIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M15 5v2m-6-2v2M5 8h14M5 8a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2v-9a2 2 0 00-2-2M5 8h14M9 12h6m-6 4h6"
    />
  </svg>
);

const AnnualsPageContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const { id: accountIdParam, name: accountName } = useParams();

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { accountId: contextAccountId },
  } = useContext(AppContext);

  // Use URL param for regular admin, AppContext for track/employee
  const accountId = accountIdParam
    ? parseInt(accountIdParam)
    : contextAccountId || null;
  const displayName = accountName ? decodeURIComponent(accountName) : null;

  // Get current year
  const currentYear = Number(moment().format('YYYY'));

  // Read ticket filter from URL params (when navigating from AnnualTicketsPage)
  const urlParams = new URLSearchParams(location.search);
  const urlTicketId = urlParams.get('ticketId');
  const urlTicketName = urlParams.get('ticketName');

  // Filter state
  const [year, setYear] = useState(currentYear);
  const [ticketFilter, setTicketFilter] = useState(
    urlTicketId
      ? {
          value: urlTicketId,
          label: urlTicketName ? decodeURIComponent(urlTicketName) : '',
        }
      : null
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const hasActiveFilters = !!(startDate || endDate);

  const toggleFilterDropdown = () => setFilterOpen(!filterOpen);

  const clearFilters = () => {
    setPendingStartDate('');
    setPendingEndDate('');
    setStartDate('');
    setEndDate('');
    setFilterOpen(false);
  };

  const applyFilters = () => {
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setFilterOpen(false);
  };

  // Search and pagination
  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Get annual tickets for filter dropdown
  const { data: ticketsData } = useGetAccountMemberships({
    year,
    accountId,
  });
  const tickets = ticketsData?.getAccountAnnualTickets ?? [];

  // Fetch annuals
  const { data, loading } = useGetAccountAnnuals({
    account_id: accountId,
    year,
    page: currentPage,
    limit: 15,
    queryString: input.queryString || null,
    ticket_id: ticketFilter ? Number(ticketFilter.value) : null,
    start_date: toGraphQLDate(startDate),
    end_date: toGraphQLDate(endDate),
  });

  const annuals = data?.getAccountAnnuals?.results ?? [];
  const totalCount = data?.getAccountAnnuals?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  // Year options
  const yearOptions = [
    { value: currentYear - 2, label: String(currentYear - 2) },
    { value: currentYear - 1, label: String(currentYear - 1) },
    { value: currentYear, label: String(currentYear) },
    { value: currentYear + 1, label: String(currentYear + 1) },
  ];

  // Ticket options for filter
  const ticketOptions = tickets.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  // Handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    updateQuery({ queryString: searchTerm, page: 1 });
  };

  // PDF Export
  const getAccountPurchaseReport = useGetAccountPurchaseReport({
    account_id: accountId,
    type: 'annual',
    start_date: toGraphQLDate(startDate),
    end_date: toGraphQLDate(endDate),
  });

  // Export
  const [isExporting, setIsExporting] = useState(false);
  const getAccountMembersReport = useGetAccountMembersReport({
    account_id: accountId,
    year,
    queryString: input.queryString || null,
    ticket_id: ticketFilter ? Number(ticketFilter.value) : null,
    type: 'annual',
    start_date: toGraphQLDate(startDate),
    end_date: toGraphQLDate(endDate),
  });

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await getAccountMembersReport();

      if (!response || response.errors) {
        toast.error('Annual holders list could not be retrieved');
        setIsExporting(false);
        return;
      }

      const results = response.data?.getAccountMembersReport?.results ?? [];

      // Only include the Reserved column when at least one holder has a seat
      const includeReserved = results.some((member) => member.seat_id);

      const dateTimeStamp = moment().format('MMM DD - YYYY h:mm A');
      const headers = `Name,Email,Phone,Annual / Season,Price,Purchase Date${
        includeReserved ? ',Reserved' : ''
      }\n`;
      const rows = results
        .map((member) => {
          const name = member.user?.formatted_name || '';
          const email = member.user?.email || '';
          const phone = member.user?.cellphone
            ? formatPhoneNumber(member.user.cellphone)
            : '';
          const ticket = member.membership?.name || '';
          const price = member.cost || '$0.00';
          const purchaseDate = member.purchase?.purchase_date
            ? moment(
                member.purchase.purchase_date,
                'MM-DD-YYYY HH:mm:ss.SSSZ'
              ).format('MMM DD - YYYY h:mm A')
            : '';
          const reserved = includeReserved ? `,"${member.seat_id || ''}"` : '';
          return `"${name}","${email}","${phone}","${ticket}","${price}","${purchaseDate}"${reserved}`;
        })
        .join('\n');

      const csv = `Annual Holders List\nExported:,${dateTimeStamp}\nAccount:,${
        displayName || ''
      }\n\n${headers}${rows}`;
      const filename = `${moment().format('MMM DD - YYYY').toUpperCase()} ${
        displayName || 'Annuals'
      } - Annual Holders List.csv`;

      var element = document.createElement('a');
      element.setAttribute(
        'href',
        'data:text/plain;charset=utf-8,' + encodeURIComponent(csv)
      );
      element.setAttribute('download', filename);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      setIsExporting(false);
    } catch (e) {
      setIsExporting(false);
    }
  };

  // Table columns
  const columns = [
    {
      field: 'name',
      header: 'Name',
      render: (member) => member.user?.formatted_name || '—',
    },
    {
      field: 'email',
      header: 'Email',
      render: (member) => member.user?.email || '—',
    },
    {
      field: 'phone',
      header: 'Phone',
      render: (member) =>
        member.user?.cellphone ? (
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatPhoneNumber(member.user.cellphone)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      field: 'ticket',
      header: 'Annual / Season',
      render: (member) => member.membership?.name || '—',
    },
    ...(!isTicketHoss
      ? [
          {
            field: 'agreements',
            header: 'Agreements',
            render: (member) => (
              <WaiverBadge $signed={member.is_signed}>
                {member.is_signed ? 'Signed' : 'Not Signed'}
              </WaiverBadge>
            ),
          },
        ]
      : []),
  ];

  // Table actions
  const actions = [
    {
      type: 'view',
      title: 'View Details',
      onClick: (member) => {
        if (member.id) {
          history.push(`${routePrefix}/annualsDetail/member/${member.id}`);
        }
      },
    },
    {
      type: 'credit',
      title: 'View Transaction',
      onClick: (member) => {
        if (member.purchase?.id) {
          history.push(
            `${routePrefix}/transactions/detail/${member.purchase.id}`
          );
        }
      },
    },
  ];

  return (
    <PageWrapper>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon>
              <AnnualsIcon />
            </PageHeaderIcon>
            <HeaderTitle>
              {displayName
                ? `${displayName} - Annual / Season Holders`
                : 'Annual / Season Holders'}
            </HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Data Table Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Search + Filters Row */}
                <div style={{ position: 'relative' }}>
                  <PageHeader>
                    <SearchInput
                      onSearch={handleSearch}
                      placeholder="Search by name..."
                    />
                    <FilterRow>
                      <FilterContainer>
                        <ThemedSelect
                          placeholder="Year"
                          options={yearOptions}
                          value={{ value: year, label: String(year) }}
                          onChange={(selected) =>
                            setYear(selected?.value || currentYear)
                          }
                        />
                      </FilterContainer>
                      {tickets.length > 0 && (
                        <FilterContainer>
                          <ThemedSelect
                            placeholder={annualLabelPlural}
                            options={ticketOptions}
                            value={ticketFilter}
                            onChange={(selected) =>
                              setTicketFilter(selected || null)
                            }
                            isClearable
                          />
                        </FilterContainer>
                      )}
                      <TableActionButton
                        onFilterClick={toggleFilterDropdown}
                        onClearClick={clearFilters}
                        hasActiveFilters={hasActiveFilters}
                      />
                    </FilterRow>
                  </PageHeader>

                  {isMobile &&
                    filterOpen &&
                    ReactDOM.createPortal(
                      <>
                        <FilterOverlay
                          show={filterOpen}
                          onClick={toggleFilterDropdown}
                        />
                        <FilterDropdown style={{ zIndex: 10001 }}>
                          <div>
                            <FilterSection>
                              <FilterTitle>Date Range</FilterTitle>
                              <FilterRange>
                                <div style={{ flex: 1 }}>
                                  <DateTimePicker
                                    value={pendingStartDate || null}
                                    onChange={(date) =>
                                      setPendingStartDate(date || '')
                                    }
                                    placeholder="Start date"
                                    showTime={false}
                                    cancelText="Clear"
                                  />
                                </div>
                                <span>to</span>
                                <div style={{ flex: 1 }}>
                                  <DateTimePicker
                                    value={pendingEndDate || null}
                                    onChange={(date) =>
                                      setPendingEndDate(date || '')
                                    }
                                    placeholder="End date"
                                    showTime={false}
                                    cancelText="Clear"
                                    minDate={
                                      pendingStartDate
                                        ? new Date(
                                            pendingStartDate + 'T00:00:00'
                                          )
                                        : null
                                    }
                                  />
                                </div>
                              </FilterRange>
                            </FilterSection>
                            <FilterActions>
                              <button onClick={clearFilters}>Clear</button>
                              <button
                                className="btn-primary"
                                onClick={applyFilters}
                              >
                                Apply Filters
                              </button>
                            </FilterActions>
                          </div>
                        </FilterDropdown>
                      </>,
                      document.body
                    )}

                  {filterOpen && !isMobile && (
                    <FilterDropdown>
                      <div>
                        <FilterSection>
                          <FilterTitle>Date Range</FilterTitle>
                          <FilterRange>
                            <div style={{ flex: 1 }}>
                              <DateTimePicker
                                value={pendingStartDate || null}
                                onChange={(date) =>
                                  setPendingStartDate(date || '')
                                }
                                placeholder="Start date"
                                showTime={false}
                                cancelText="Clear"
                              />
                            </div>
                            <span>to</span>
                            <div style={{ flex: 1 }}>
                              <DateTimePicker
                                value={pendingEndDate || null}
                                onChange={(date) =>
                                  setPendingEndDate(date || '')
                                }
                                placeholder="End date"
                                showTime={false}
                                cancelText="Clear"
                                minDate={
                                  pendingStartDate
                                    ? new Date(pendingStartDate + 'T00:00:00')
                                    : null
                                }
                              />
                            </div>
                          </FilterRange>
                        </FilterSection>
                        <FilterActions>
                          <button onClick={clearFilters}>Clear</button>
                          <button
                            className="btn-primary"
                            onClick={applyFilters}
                          >
                            Apply Filters
                          </button>
                        </FilterActions>
                      </div>
                    </FilterDropdown>
                  )}
                </div>

                <DataTable
                  columns={columns}
                  data={annuals}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No annual / season holders found"
                  emptySubtext="Try adjusting your filters"
                  emptyIcon={
                    <svg
                      width="48"
                      height="48"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ marginBottom: '16px', opacity: 0.5 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M15 5v2m-6-2v2M5 8h14M5 8a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2v-9a2 2 0 00-2-2M5 8h14M9 12h6m-6 4h6"
                      />
                    </svg>
                  }
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            <QuickActions
              title="Export"
              actions={[
                {
                  label: 'Export CSV',
                  disabled: isExporting || totalCount === 0,
                  disabledReason: isExporting
                    ? 'Export in progress...'
                    : 'No data to export',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => handleExport(),
                },
                {
                  component: (
                    <PrintAnnualPurchaseReport
                      annualTicketName={displayName || 'Annuals'}
                      accountName={displayName || 'Annuals'}
                      account_id={accountId}
                      fetchReport={getAccountPurchaseReport}
                      responseKey="getAccountPurchaseReport"
                      button={
                        <ButtonV2
                          elevated
                          icon={
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                            </svg>
                          }
                        >
                          Export PDF
                        </ButtonV2>
                      }
                    />
                  ),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>
      </Container>
    </PageWrapper>
  );
};

// Wrap with theme provider
const AnnualsPage = () => (
  <EnhancedThemeProvider>
    <AnnualsPageContent />
  </EnhancedThemeProvider>
);

export default AnnualsPage;
